import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { motion } from 'framer-motion';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-pink to-brand-purple flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl font-bold">Verifying access...</p>
        </motion.div>
      </div>
    );
  }

  // If not logged in, redirect to admin login
  if (!user) {
    return <Navigate to="/admin" replace />;
  }

  // If logged in but not an admin, show access denied
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-3xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-700 mb-2">
            Your account <span className="font-semibold">({user.email})</span> is not authorized to access the admin panel.
          </p>
          <p className="text-gray-600 text-sm mb-6">
            Please contact the administrator if you need access.
          </p>
          <div className="space-y-3">
            <a
              href="/"
              className="block bg-brand-blue text-white px-6 py-3 rounded-bubble font-bold hover:bg-opacity-90 transition-all"
            >
              Go to Home
            </a>
            <button
              onClick={() => window.location.href = '/admin'}
              className="block w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-bubble font-bold hover:bg-gray-300 transition-all"
            >
              Try Different Account
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // User is authenticated and is an admin
  return <>{children}</>;
};

export default ProtectedRoute;

